/* eslint camelcase: 0, @typescript-eslint/camelcase: 0 */
/* globals mixpanel, mp */

import {apiClient} from '../../../../util/api-client';
import {CHANGE_TYPE_SENT_FEEDBACK} from '../../../../widgets/alerts/smart-hub/constants';
import {Alert, Anomaly, Contour} from '../../../../widgets/alerts/smart-hub/models';
import {
  expandToAnomalyDetails,
  expandToContourDetails,
  getAlertUrl,
} from '../../../../widgets/alerts/smart-hub/utils/common';

export default class SmartHub {
  public static from_json(params, shared = false) {
    if (!params.alert_type) {
      return null;
    }

    if (!params.alert_id) {
      return null;
    }

    return new SmartHub({
      alert_id: params.alert_id,
      alert_type: params.alert_type,
      shared,
    });
  }

  public shared: boolean;
  private _alertCallbacks: any[][];
  private _alertType: string;
  private _alertId: string;
  private _alert: Anomaly | Contour;

  constructor({alert_id, alert_type, shared = false}: {alert_id: string; alert_type: string; shared: boolean}) {
    this._alertCallbacks = [];
    this._alertType = alert_type;
    this._alert = null;
    this.alertId = alert_id; // trigger a load
    this.shared = shared;
  }

  set alertId(val) {
    this._alertId = val;
    this.load();
  }

  set alert(val) {
    this._alert = val;

    for (const [fn, ctx] of this._alertCallbacks) {
      fn.apply(ctx, val);
    }
  }

  get alert() {
    return this._alert;
  }
  get alert_type() {
    return this._alertType;
  }
  get alert_id() {
    return this._alertId;
  }
  get projectId() {
    return mp.report.globals.project_id;
  }

  public onAlertChange(callback, context) {
    this._alertCallbacks.push([callback, context]);
  }

  public to_json() {
    return {
      alert_id: this.alert_id,
      alert_type: this.alert_type,
    };
  }

  public changed_detail_alert(ev, {silent = false} = {}) {
    const alert = this.alert;
    const newDetailAlert = ev.detail.alert;
    if (newDetailAlert.alertId !== alert.alertId || ev.detail.type !== CHANGE_TYPE_SENT_FEEDBACK) {
      // These conditions shouldn't occur, but safe guard against them just in case.
      return;
    }

    // TODO(mack): Lots of duplication here that could probably be cleaned up.
    if (`anomaly` in alert && alert.anomaly) {
      const newDetail = newDetailAlert.anomaly.anomalyDetail;
      alert.anomaly.anomalyDetails.forEach(function(detail) {
        if (detail.anomalyDetailId === newDetail.anomalyDetailId) {
          detail.sentFeedbackAtMs = newDetail.sentFeedbackAtMs;
        }
      });
    } else if (`contour` in alert && alert.contour) {
      const newDetail = newDetailAlert.contour.contourDetail;
      alert.contour.contourDetails.forEach(function(detail) {
        if (detail.contourDetailId === newDetail.contourDetailId) {
          detail.sentFeedbackAtMs = newDetail.sentFeedbackAtMs;
        }
      });
    }
    if (silent) {
      this._alert = alert;
    } else {
      this.alert = alert;
    }
  }

  private load() {
    const alert_id = this.alert_id;
    if (!alert_id) {
      return;
    }

    const url = getAlertUrl({
      projectId: this.projectId,
      alertId: alert_id,
    });

    apiClient.get(url).then((alert: any) => {
      this.alert = alert;
      if (this.shared) {
        const smartHubAlert = alert;
        let detailAlert;
        if (smartHubAlert.anomaly) {
          detailAlert = expandToAnomalyDetails(smartHubAlert)[0];
        } else if (smartHubAlert.contour) {
          detailAlert = expandToContourDetails(smartHubAlert)[0];
        }
        if (detailAlert) {
          const alertInstance = Alert.create(detailAlert, this.projectId);
          let explainProps = {};
          if (smartHubAlert.explains) {
            const explain = (alertInstance as Anomaly).explains.find((exp) => {
              return (
                exp.property === smartHubAlert.explains.property &&
                exp.propertyValue === smartHubAlert.explains.propertyValue
              );
            });

            if (explain) {
              explainProps = {
                explain_property: explain.property,
                explain_value: explain.propertyValue,
                explain_priority: explain.priority,
                explain_type: explain.propertyType,
              };
            } else {
              console.error(`expected to find a matching explain but could not, explain properties not tracked`);
            }
          }
          mixpanel.track(`Smart Hub: Viewed Shared Alert`, {
            ...alertInstance.trackingProperties,
            alert_view: smartHubAlert.explains ? `Explain View` : `Card View`,
            ...explainProps,
          });
        }
      }
    });
  }
}
